import React, { Fragment, useState, useEffect, useRef } from "react";
import profilePic from "../../assets/images/profile-pic.png";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as httpService from "../../services/httpService";
import { Employee_Api_EndPoints } from "../../global/end-points";
import * as userService from "../../utils/userService";
import useActivityLogger from "../../utils/useActivityLogger"; // ✅ import logger

function Header() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const fileRef = useRef();
  const didMount = useRef(false);

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [imageDataUrl, setImageDataUrl] = useState(profilePic);
  const [, forceRender] = useState(0);

  const { logActivity } = useActivityLogger({ moduleCode: "DPY" });

  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

  useEffect(() => {
    const onPrivs = () => forceRender((x) => x + 1);
    window.addEventListener("privileges-updated", onPrivs);
    return () => window.removeEventListener("privileges-updated", onPrivs);
  }, []);

  useEffect(() => {
    if (!didMount.current) {
      const storedLanguage = localStorage.getItem("selectedLanguage");
      if (storedLanguage && storedLanguage !== selectedLanguage) {
        setSelectedLanguage(storedLanguage);
        i18n.changeLanguage(storedLanguage);
      }
      getProfilePic();
    }
    return () => {
      didMount.current = true;
    };
  }, [location.pathname]);

  const handleLanguageChange = (event) => {
    const language = event.target.value;
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
    localStorage.setItem("selectedLanguage", language);

    // ✅ log language change with OBO prefix if applicable
    logActivity({
      activity: "LANGUAGE_CHANGE",
      description: `${isOBO ? "OBO-" : ""}Changed language to ${language}`,
      component: `${isOBO ? "OBO.HDR.LanguageSelector" : "HDR.LanguageSelector"}`,
    });
  };

  const handleFileChange = async (event) => {
    event.preventDefault();
    const effectiveUserId =
      sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");

    if (effectiveUserId) {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      formData.append("userId", Number(effectiveUserId));

      fetch(Employee_Api_EndPoints.FILE_UPLOAD, {
        method: "POST",
        body: formData,
        headers: {
          AccessToken: userService.GetUserToken(),
        },
      })
        .then((response) => {
          if (response.status === 200) {
            getProfilePic();

            // ✅ log successful avatar upload
            logActivity({
              activity: "FILE_UPLOAD",
              description: `${isOBO ? "OBO-" : ""}Profile picture updated`,
              component: `${isOBO ? "OBO.HDR.ProfilePic" : "HDR.ProfilePic"}`,
            });
          }
          fileRef.current.value = null;
        })
        .catch((error) => {
          console.error("Error:", error);

          // ✅ log failed avatar upload
          logActivity({
            activity: "FILE_UPLOAD_ERROR",
            description: `${isOBO ? "OBO-" : ""}Profile picture upload failed`,
            component: `${isOBO ? "OBO.HDR.ProfilePic" : "HDR.ProfilePic"}`,
          });
        });
    } else {
      userService.ClearCookies();
    }
  };

  const getProfilePic = () => {
    const effectiveUserId =
      sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");

    if (effectiveUserId) {
      const body = { UserId: Number(effectiveUserId) };

      httpService.Get(Employee_Api_EndPoints.GET_PROFILE_PIC, body).then(
        (resp) => {
          if (resp.code === "ERR_BAD_REQUEST" || resp.data?.message) {
            setImageDataUrl(profilePic);
          } else if (resp.data) {
            setImageDataUrl("data:image/png;base64," + resp.data);
          }
        },
        (err) => console.error("Error", err)
      );
    } else {
      userService.ClearCookies();
    }
  };

  function hasOBOPrivilege() {
    try {
      const raw = sessionStorage.getItem("Privileges");
      if (!raw) return false;

      const { privileges = [] } = JSON.parse(raw) || {};
      const target = new Set(["loginonbehalf", "onbehalflogin", "obo"]);

      return privileges.some((p) => {
        const mod = (p?.moduleName || "").toLowerCase();
        const allowed =
          p?.canRead === true ||
          p?.canExecute === true ||
          p?.canCreate === true;
        return target.has(mod) && allowed;
      });
    } catch {
      return false;
    }
  }

  // Names for display
  const realUserName = sessionStorage.getItem("UserName") ?? "Unknown User";
  const onBehalfUserName = sessionStorage.getItem("OnBehalfUserName") || null;
  const isRegisterCardPage = location.pathname.toLowerCase() === "/registercard";

  return (
    <Fragment>
      <div className="dashboard-header">
        <h2 className="page-heading">{t("dashboard")}</h2>

        <div className="header-right">
          {/* Show "Login On Behalf" link only if NOT already on behalf */}
          {!onBehalfUserName && !isRegisterCardPage && hasOBOPrivilege() && (
            <div className="login-on-behalf-container">
              <Link
                to="/login-on-behalf"
                className="login-on-behalf-label"
                onClick={() =>
                  logActivity({
                    activity: "NAVIGATION",
                    description: `${isOBO ? "OBO-" : ""}Navigated to Login On Behalf`,
                    component: `${isOBO ? "OBO.HDR.LoginOnBehalf" : "HDR.LoginOnBehalf"}`,
                  })
                }
              >
                {t("Login On Behalf")}
              </Link>
            </div>
          )}

          {/* Language container */}
          <div className="languages-container">
            <select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              className="language-selection"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </select>
          </div>
        </div>

        <div className="user-profile">
          <div
            className={`user-names ${!onBehalfUserName ? "user-names-center" : ""
              }`}
          >
            <span className="real-user">{realUserName}</span>
            {onBehalfUserName && (
              <div className="onbehalf-user">
                (On behalf of: {onBehalfUserName})
              </div>
            )}
          </div>
          <div className="user-profile-pic">
            <div className="user-profile-pic-inner">
              <img src={imageDataUrl} alt="Profile" />
            </div>

            <div className="profile-menu-container">
              <div className="profile-menu-item">
                <input
                  type="file"
                  name="file"
                  id="file"
                  className="inputfile"
                  onChange={handleFileChange}
                  ref={fileRef}
                />
                <label htmlFor="file">{t("ChangeAvtar")}</label>
              </div>
              <Link
                to="/logout"
                className="profile-menu-item"
                onClick={() =>
                  logActivity({
                    activity: "LOGOUT",
                    description: `${isOBO ? "OBO-" : ""}User clicked logout`,
                    component: `${isOBO ? "OBO.HDR.Logout" : "HDR.Logout"}`,
                  })
                }
              >
                {t("Logout")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default Header;
